import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Users, Star, Phone, MessageCircle, ChevronRight, Home } from 'lucide-react';
import Card from '../components/Card';
import { useEscortsData } from '../hooks/useEscortsData';
import { 
  findLocationSEO, 
  getCitiesForProvinceSEO,
  getProvincesForRegionSEO,
  translateLocationSEO,
  TranslatedLocationSEO
} from '../data/seoTemplates';
import { Escort } from '../types';
import { useSEO } from '../hooks/useSEO';
import { createEscortUrl } from '../utils/urlUtils';
import {
  getProvinceByCity,
  getAllCities
} from '../data/cities';
import { useCountryCode } from '../hooks/useCountryCode';
import { useSEOContext } from '../contexts/SEOContext';
import { useLocationContext } from '../contexts/LocationContext';
import { useCountryContext } from '../contexts/CountryContext';
import { useDisclaimer } from '../contexts/DisclaimerContext';
import { useTranslation } from '../hooks/useTranslation';
import { getCountryByCity } from '../config/cityCountryMapping';
import LoadingSpinner from '../components/atoms/LoadingSpinner';
//import {debugLog} from "../utils/log.ts";

const LocationPage: React.FC = () => {
  const { location } = useParams<{ location: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasAcceptedDisclaimer } = useDisclaimer();
  const { countryCode } = useCountryCode();
  const { selectedCountry, setSelectedCountry } = useCountryContext();
  const { escorts, loading, error } = useEscortsData();
  const { setSeoData } = useSEOContext();
  const [filteredEscorts, setFilteredEscorts] = useState<Escort[]>([]);
  const [translatedSeoData, setTranslatedSeoData] = useState<TranslatedLocationSEO | null>(null);
  const {
    relatedCities,
    setRelatedCities,
    relatedProvinces,
    setRelatedProvinces,
    allCities,
    setAllCities
  } = useLocationContext();

  const sortEscortsByPriorityAndRandomize = (escorts: Escort[]): Escort[] => {
    const vipEscorts = escorts.filter(escort => escort.featured).sort(() => Math.random() - 0.5);
    const vetrinaEscorts = escorts.filter(escort => !escort.featured && escort.isVetrina).sort(() => Math.random() - 0.5);
    const nonVetrinaEscorts = escorts.filter(escort => !escort.featured && !escort.isVetrina).sort(() => Math.random() - 0.5);
    return [...vipEscorts, ...vetrinaEscorts, ...nonVetrinaEscorts];
  };

  // Imposta il paese basato sulla città IMMEDIATAMENTE al mount
  useEffect(() => {
    if (location) {
      const decodedLocation = decodeURIComponent(location);
      const requiredCountry = getCountryByCity(decodedLocation);
      
      // Imposta sempre il paese richiesto dalla città, anche se uguale
      // Questo assicura che il paese sia impostato PRIMA del caricamento dati
      if (requiredCountry !== selectedCountry) {
        console.log(`🔄 LocationPage: Cambio paese da ${selectedCountry} a ${requiredCountry} per città ${decodedLocation}`);
        setSelectedCountry(requiredCountry);
      }
    }
  }, [location]); // Rimuovo selectedCountry dalle dipendenze per evitare loop

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    //debugLog('DetailPage mounted - scrolled to top (first time only)');

    const cities = getAllCities();
    setAllCities(cities);
    
    const decodedLocation = decodeURIComponent(location? location : '');
    
    // Trova i dati SEO per la location usando la nuova funzione
    const locationSEO = findLocationSEO(decodedLocation);
    setSeoData(locationSEO);
    
    // Traduci i dati SEO se disponibili
    if (locationSEO) {
      const translated = translateLocationSEO(locationSEO, t);
      setTranslatedSeoData(translated);
    } else {
      setTranslatedSeoData(null);
    }
    
    // Gestisci i link correlati in base al tipo di location
    if (locationSEO) {
      if (locationSEO.locationType === 'city') {
        // Se è una città, mostra altre città della stessa provincia
        const province = getProvinceByCity(decodedLocation);
        if (province) {
          const cities = getCitiesForProvinceSEO(province.name)
            .filter(city => city.toLowerCase() !== decodedLocation.toLowerCase())
            .slice(0, 8);
          setRelatedCities(cities);
        }
      } else if (locationSEO.locationType === 'province') {
        // Se è una provincia, mostra le città principali della provincia
        const cities = getCitiesForProvinceSEO(decodedLocation).slice(0, 10);
        setRelatedCities(cities);
      } else if (locationSEO.locationType === 'region') {
        // Se è una regione, mostra le province della regione
        const provinces = getProvincesForRegionSEO(decodedLocation);
        setRelatedProvinces(provinces);
      }
    }
  }, [location, setSeoData, setRelatedCities, setRelatedProvinces, setAllCities, t]);

  // Filtra le escort per location quando i dati sono disponibili
  useEffect(() => {
    if (!location || escorts.length === 0) {
      console.log(`⏸️ Skip filtro - location: ${!!location}, escorts: ${escorts.length}`);
      return;
    }

    const decodedLocation = decodeURIComponent(location);
    const requiredCountry = getCountryByCity(decodedLocation);
    
    // Non filtrare se il paese non è ancora quello giusto
    if (requiredCountry !== selectedCountry) {
      console.log(`❌ Paese non allineato: richiede ${requiredCountry}, attuale ${selectedCountry} - azzero filtrate`);
      setFilteredEscorts([]);
      return;
    }
    
    const filtered = escorts.filter(escort =>
      escort.location.toLowerCase().includes(decodedLocation.toLowerCase()) &&
      (!countryCode || !escort.blocked?.includes(countryCode))
    );

    console.log(`✅ Filtrate ${filtered.length} escort per ${decodedLocation} (${selectedCountry}), totali: ${escorts.length}`);
    
    // Applica l'ordinamento alle escort filtrate
    const sortedEscorts = sortEscortsByPriorityAndRandomize(filtered);
    setFilteredEscorts(sortedEscorts);
  }, [location, escorts, countryCode, selectedCountry]);

  useSEO({
    title: location ? (translatedSeoData?.title || 'EscortFun.net') : `${t('locationPage.allCitiesTitle')} - EscortFun.net`,
    description: location ? (translatedSeoData?.description || 'Directory escort premium') : t('locationPage.allCitiesTitle'),
    keywords: translatedSeoData?.keywords || [],
    ogUrl: location ? `https://escortfun.net/location/${location}` : 'https://escortfun.net/location',
    canonicalUrl: location ? `https://escortfun.net/location/${location}` : 'https://escortfun.net/location'
  });

  // Non mostrare contenuto se il disclaimer non è stato accettato
  if (!hasAcceptedDisclaimer) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner className="mx-auto mb-4" />
        </div>
      </div>
    );
  }

  /* const handleSearch = (query: string, searchLocation: string) => {
    navigate(`/escorts?search=${query}&location=${searchLocation}`);
  }; */
  
  const handleViewProfile = (id: string) => {
    const escort = escorts.find(e => e.id === id);
    if (escort && escort.phone) {
      const url = createEscortUrl(escort as { phone: string; name: string; location: string });
      navigate(url);
    }
  };

  const handleFavorite = (id: string) => {
    /* Add to favorites functionality */
    console.log(id);
  };

  if (!translatedSeoData) {
    return (
      <div className="container mx-auto px-2 py-16">
        <h1 className="text-3xl font-bold text-center text-gray-800 mb-10">{t('locationPage.allCitiesTitle')}</h1>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
          {allCities.map(city => (
            <div
              key={city}
              className="p-3 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer text-center"
              onClick={() => navigate(`/location/${encodeURIComponent(city)}`)}
            >
              <span className="text-gray-800">{city}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Breadcrumbs */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-2 py-3">
          <nav className="flex items-center space-x-2 text-sm">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center text-gray-500 hover:text-pink-500 transition-colors"
              aria-label={t('locationPage.homeAriaLabel')}
            >
              <Home className="h-4 w-4 mr-1" />
              {t('locationPage.homeLink')}
            </button>
            {translatedSeoData.regionName == null && (
              <>
                <ChevronRight className="h-4 w-4 text-gray-400"/>
                <button
                  onClick={() => navigate('/')}
                  className="text-gray-500 hover:text-pink-500 transition-colors"
                  aria-label={t('locationPage.regionsAriaLabel')}
                >
                  {t('locationPage.regionsLink')}
                </button>
              </>
            )}
            {translatedSeoData.regionName && (
              <>
                <ChevronRight className="h-4 w-4 text-gray-400" />
                <button 
                  onClick={() => navigate(`/location/${encodeURIComponent(translatedSeoData.regionName)}`)}
                  className="text-gray-500 hover:text-pink-500 transition-colors"
                  aria-label={t('locationPage.regionAriaLabel', { regionName: translatedSeoData.regionName })}
                >
                  {translatedSeoData.regionName}
                </button>
              </>
            )}
            <ChevronRight className="h-4 w-4 text-gray-400" />
            <span className="text-pink-500 font-medium">{translatedSeoData.cityName}</span>
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-2 py-4 md:py-12 md:px-2">
        <div className="flex flex-col lg:flex-row gap-2">
          {/* Content principale */}
          <div className="lg:w-2/3">
            {/* Sezione Escort */}
            <div className="mb-12">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  {t('locationPage.escortsInCity', { cityName: translatedSeoData.cityName })}
                </h2>
              </div>

              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <LoadingSpinner />
                  <span className="ml-3 text-gray-600">{t('locationPage.loadingEscorts')}</span>
                </div>
              ) : error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center mb-8">
                  <p className="text-red-600 font-medium">{t('locationPage.loadingError')}</p>
                  <p className="text-red-500 text-sm mt-2">{error}</p>
                </div>
              ) : filteredEscorts.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 xl:grid-cols-3 md:gap-2 mb-8">
                  {filteredEscorts.map((escort) => (
                    <Card 
                      key={escort.id}
                      escort={escort}
                      onViewProfile={handleViewProfile}
                      onFavorite={handleFavorite}
                      source="location"
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200 rounded-lg p-8 text-center mb-8">
                  <Users className="h-16 w-16 text-pink-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">
                    {t('locationPage.noEscortsTitle', { cityName: translatedSeoData.cityName })}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {t('locationPage.noEscortsMessage')}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2 justify-center">
                    <button
                      onClick={() => navigate('/escorts')}
                      className="bg-pink-500 hover:bg-pink-600 text-white px-6 py-3 rounded-lg transition-colors font-medium"
                      aria-label={t('locationPage.exploreAllEscortsAriaLabel')}
                    >
                      {t('locationPage.exploreAllEscortsButton')}
                    </button>
                    <button
                      onClick={() => navigate('/auth')}
                      className="bg-white border-2 border-pink-500 text-pink-500 hover:bg-pink-500 hover:text-white px-6 py-3 rounded-lg transition-colors font-medium"
                      aria-label={t('locationPage.postAdAriaLabel')}
                    >
                      {t('locationPage.postAdButton')}
                    </button>
                  </div>
                </div>
              )}
            </div>            {/* Contenuto SEO */}

            {/* Stats Section */}
            <div className="bg-white py-8 shadow-sm">
              <div className="container mx-auto px-2">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
                  <div className="flex flex-col items-center">
                    <Users className="h-8 w-8 text-pink-500 mb-2" />
                    <div className="text-2xl font-bold text-gray-800">{filteredEscorts.length}</div>
                    <div className="text-gray-600">{t('locationPage.availableEscorts')}</div>
                  </div>
                  <div className="flex flex-col items-center">
                    <Star className="h-8 w-8 text-pink-500 mb-2" />
                    <div className="text-2xl font-bold text-gray-800">100%</div>
                    <div className="text-gray-600">{t('common.profili_verificati')}</div>
                  </div>
                  <div className="flex flex-col items-center">
                    <Phone className="h-8 w-8 text-pink-500 mb-2" />
                    <div className="text-2xl font-bold text-gray-800">24/7</div>
                    <div className="text-gray-600">{t('locationPage.customerSupport')}</div>
                  </div>
                  <div className="flex flex-col items-center">
                    <MessageCircle className="h-8 w-8 text-pink-500 mb-2" />
                    <div className="text-2xl font-bold text-gray-800">{t('locationPage.discretionGuaranteed')}</div>
                    <div className="text-gray-600">{t('locationPage.guaranteed')}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-8">
              <div className="prose max-w-none">
                {translatedSeoData.content.map((paragraph: string, index: number) => {
                  if (paragraph.startsWith('##')) {
                    return (
                      <h2 key={index} className="text-2xl font-bold text-gray-800 mt-8 mb-4">
                        {paragraph.replace('## ', '')}
                      </h2>
                    );
                  } else if (paragraph.startsWith('-')) {
                    // Lista
                    return (
                      <li key={index} className="text-gray-700 mb-2 ml-4">
                        {paragraph.replace('- ', '')}
                      </li>
                    );
                  } else {
                    return (
                      <p key={index} className="text-gray-700 mb-4 leading-relaxed">
                        {paragraph}
                      </p>
                    );
                  }
                })}
                
                {/* Sezione extra se non ci sono escort disponibili */}
                {filteredEscorts.length === 0 && (
                  <div className="mt-8 p-6 bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200 rounded-lg">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">
                      {t('locationPage.areYouEscortTitle', { cityName: translatedSeoData.cityName })}
                    </h2>
                    <p className="text-gray-700 mb-4 leading-relaxed">
                      <strong>EscortFun.net</strong> {t('locationPage.areYouEscortDescription', { cityName: translatedSeoData.cityName })}
                    </p>
                    <ul className="text-gray-700 mb-4 ml-4 space-y-2">
                      <li>• {t('locationPage.benefitsList.visibility', { cityName: translatedSeoData.cityName })}</li>
                      <li>• {t('locationPage.benefitsList.clients')}</li>
                      <li>• {t('locationPage.benefitsList.appointments')}</li>
                      <li>• {t('locationPage.benefitsList.environment')}</li>
                      <li>• {t('locationPage.benefitsList.marketing')}</li>
                    </ul>
                    <p className="text-gray-700 mb-6 leading-relaxed">
                      {t('locationPage.platformDescription', { cityName: translatedSeoData.cityName })}
                    </p>
                    <div className="text-center">
                      <button
                        onClick={() => navigate('/auth')}
                        className="bg-pink-500 hover:bg-pink-600 text-white px-8 py-3 rounded-lg font-medium transition-colors inline-flex items-center"
                      >
                        <Users className="h-5 w-5 mr-2" />
                        {t('locationPage.publishFreeAdButton')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:w-1/3">
            {/* Info Location */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                <MapPin className="h-6 w-6 text-pink-500 mr-2" />
                {t('locationPage.informationAbout', { cityName: translatedSeoData.cityName })}
              </h3>
              <div className="space-y-3">
                <div>
                  <span className="font-medium text-gray-700">{t('locationPage.region')}:</span>
                  <span className="text-gray-600 ml-2">{translatedSeoData.regionName}</span>
                </div>
                {translatedSeoData.provinceName && (
                  <div>
                    <span className="font-medium text-gray-700">{t('locationPage.province')}:</span>
                    <span className="text-gray-600 ml-2">{translatedSeoData.provinceName}</span>
                  </div>
                )}
                <div>
                  <span className="font-medium text-gray-700">{t('locationPage.availableEscortsLabel')}:</span>
                  <span className="text-pink-500 font-semibold ml-2">{filteredEscorts.length}</span>
                </div>
              </div>
            </div>

            {/* CTA Box */}
            <div className="bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg p-6 mb-6">
              <h3 className="text-xl font-semibold mb-3">{t('locationPage.postAdTitle')}</h3>
              <p className="text-pink-100 mb-4">
                {t('locationPage.postAdDescription', { cityName: translatedSeoData.cityName })}
              </p>
              <button 
                onClick={() => navigate('/auth')}
                className="bg-white text-pink-600 hover:bg-gray-100 px-2 py-2 rounded-lg font-medium transition-colors w-full"
              >
                {t('locationPage.registerNowButton')}
              </button>
            </div>

            {/* Keywords per SEO */}
            {/*
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Cerca Anche</h3>
              <div className="flex flex-wrap gap-2">
                {translatedSeoData.keywords.slice(0, 8).map((keyword: string, index: number) => {
                  const retrieveCity = keyword.split(' a ')[1];
                  return (
                    <button
                      key={index}
                      onClick={() => navigate(`/escorts?search=&location=${retrieveCity}`)}
                      className="bg-gray-100 hover:bg-pink-100 text-gray-700 hover:text-pink-600 px-3 py-1 rounded-full text-sm transition-colors"
                    >
                      {keyword}
                    </button>
                  );
                })}
              </div>
            </div>
            */}

            {/* Link correlati basati sul tipo di location */}
            {(relatedCities.length > 0 || relatedProvinces.length > 0) && (
              <div className="bg-white rounded-lg shadow-md p-6">
                {relatedCities.length > 0 && (
                  <>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">
                      {translatedSeoData.locationType === 'city' ? t('locationPage.otherNearbyCities') : 
                       translatedSeoData.locationType === 'province' ? t('locationPage.mainCities') : t('locationPage.cities')}
                    </h3>
                    <div className="grid grid-cols-2 gap-2 mb-6">
                      {relatedCities.map((city, index) => (
                        <button
                          key={index}
                          onClick={() => navigate(`/location/${encodeURIComponent(city)}`)}
                          className="bg-gradient-to-r from-pink-50 to-purple-50 hover:from-pink-100 hover:to-purple-100 text-gray-700 hover:text-pink-600 px-2 py-2 rounded-lg text-sm font-medium transition-all duration-200 border border-pink-100 hover:border-pink-200 truncate"
                          title={city}
                        >
                          <MapPin className="h-4 w-4 inline mr-1 flex-shrink-0" />
                          <span className="truncate">{city}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
                
                {relatedProvinces.length > 0 && (
                  <>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('locationPage.regionProvinces')}</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {relatedProvinces.map((province, index) => (
                        <button
                          key={index}
                          onClick={() => navigate(`/location/${encodeURIComponent(province)}`)}
                          className="bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 text-gray-700 hover:text-blue-600 px-2 py-2 rounded-lg text-sm font-medium transition-all duration-200 border border-blue-100 hover:border-blue-200"
                        >
                          <MapPin className="h-4 w-4 inline mr-1" />
                          {t('locationPage.provinceOf', { province })}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Schema.org JSON-LD per SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": translatedSeoData.title,
            "description": translatedSeoData.description,
            "url": `https://escortfun.net/location/${location}`,
            "isPartOf": {
              "@type": "WebSite",
              "name": "EscortFun.net",
              "url": "https://escortfun.net"
            },
            "about": {
              "@type": "Place",
              "name": translatedSeoData.cityName,
              "addressRegion": translatedSeoData.regionName
            },
            "keywords": translatedSeoData.keywords.join(", ")
          })
        }}
      />
    </div>
  );
};

export default LocationPage;
